<template>
  <div>
    <!-- Global Settings Card -->
    <b-card class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="font-weight-semibold mb-2">
          {{ t("dmp.areaInformation.globalSettings") }}
        </h6>
        <div class="d-flex gap-1 justify-content-end align-items-center">
          <b-button
            v-if="!isTemplateMode"
            variant="primary"
            @click="saveGlobalSettings"
            :disabled="isLoading || isSavingGlobal"
          >
            <b-spinner small v-if="isSavingGlobal" class="mr-1" />
            <template v-if="source === 'default'">
              {{ t("dmp.areaInformation.saveGlobalSettings") }}
            </template>
            <template v-else>
              {{ t("actions.save") }}
            </template>
          </b-button>
        </div>
      </div>
      <validation-observer ref="refFormObserver">
        <b-form>
          <b-row>
            <b-col cols="12" md="4">


              <b-form-group
                :label="t('dmp.areaInformation.exitDelay')"
                label-for="exitDelay"
              >
                <b-form-input
                  id="exitDelay"
                  v-model="globalSettings.exitDelay"
                  type="number"
                  min="0"
                  max="999"
                  :disabled="isLoading"
                />
              </b-form-group>

              <b-form-group
                :label="t('dmp.areaInformation.earlyMorningAmbush')"
                label-for="earlyMorningAmbush"
              >
                <b-form-input
                  id="earlyMorningAmbush"
                  v-model="globalSettings.earlyMorningAmbush"
                  type="number"
                  min="0"
                  max="999"
                  :disabled="isLoading"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="4">
              <div class="d-flex flex-column gap-1 mt-2">


                <b-form-checkbox
                  v-model="globalSettings.closingCheck"
                  :disabled="isLoading"
                >
                  {{ t("dmp.areaInformation.closingCheck") }}
                </b-form-checkbox>
                
                <b-form-checkbox
                  v-model="globalSettings.areaSchedules"
                  :disabled="isLoading"
                >
                  {{ t("dmp.areaInformation.areaSchedules") }}
                </b-form-checkbox>
                
                <b-form-checkbox
                  v-model="globalSettings.closingCode"
                  :disabled="isLoading"
                >
                  {{ t("dmp.areaInformation.closingCode") }}
                </b-form-checkbox>
                
                <b-form-checkbox v-model="globalSettings.anyBypass" :disabled="isLoading">
                  {{ t("dmp.areaInformation.anyBypass") }}
                </b-form-checkbox>


              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>

    <!-- Areas Table Card -->
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ t("dmp.areaInformation.areas") }}</h5>
              <div class="d-flex gap-1">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="addNewArea"
                  :disabled="isLoading"
                  class="text-capitalize"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ t("dmp.areaInformation.addArea") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-4">
              <b-spinner
                variant="primary"
                :label="t('dmp.areaInformation.loadingAreas')"
              ></b-spinner>
              <p class="mt-2 mb-0">{{ t("dmp.areaInformation.loadingAreas") }}</p>
            </div>

            <b-row v-if="!isLoading">
              <b-col cols="5" class="table-column">
                <!-- Areas Table -->
                <b-table
                  :items="areas"
                  :fields="areaTableFields"
                  responsive
                  striped
                  hover
                  show-empty
                  :empty-text="t('dmp.areaInformation.noAreasConfigured')"
                  selectable
                  select-mode="single"
                  @row-selected="onRowSelected"
                  sort-by="sourceId"
                  :sort-desc="false"
                  class="mb-0"
                >
                  <template #cell(sourceId)="data">
                    {{ parseInt(data.item.sourceId) }}
                  </template>

                  <template #cell(name)="data">
                    {{ data.item.name }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="7" class="details-column">
                <!-- Selected Area Details Card -->
                <b-card v-if="selectedArea" no-body>
                  <b-card-header class="mb-1 card-header-custom pt-0">
                    <h6 class="mb-0">{{ t("dmp.areaInformation.areaDetails") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="areaFormObserver">
                      <b-form @submit.prevent="saveArea">
                        <b-row>
                          <b-col cols="12" md="6">
                            <validation-provider
                              #default="{ errors }"
                              :name="t('dmp.areaInformation.areaNumber')"
                              rules="required|integer|min_value:1|max_value:99"
                            >
                              <b-form-group :label="t('dmp.area')" label-for="areaNumber">
                                <b-form-input
                                  id="areaNumber"
                                  v-model.number="selectedArea.sourceId"
                                  type="number"
                                  min="1"
                                  max="99"
                                  :state="errors.length > 0 ? false : null"
                                  :placeholder="t('dmp.areaInformation.areaNumber')"
                                  :disabled="isEditingArea"
                                />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12" md="6">
                            <validation-provider
                              #default="{ errors }"
                              :name="t('dmp.areaInformation.areaName')"
                              rules="required|max:50"
                            >
                              <b-form-group
                                :label="t('dmp.areaInformation.areaName')"
                                label-for="areaName"
                              >
                                <b-form-input
                                  id="areaName"
                                  v-model="selectedArea.name"
                                  type="text"
                                  :state="errors.length > 0 ? false : null"
                                  :placeholder="t('dmp.areaInformation.areaName')"
                                  maxlength="50"
                                />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12" md="6">
                            <b-form-group
                              :label="t('dmp.areaInformation.accountNumber')"
                              label-for="accountNumber"
                            >
                              <b-form-input
                                id="accountNumber"
                                v-model="selectedArea.accountNumber"
                                type="number"
                                min="0"
                                max="99999"
                                :placeholder="t('dmp.areaInformation.accountNumber')"
                              />
                            </b-form-group>
                          </b-col>

                          <b-col cols="12" md="6">
                            <b-form-group
                              :label="t('dmp.areaInformation.badZones')"
                              label-for="badZones"
                            >
                              <b-form-select
                                id="badZones"
                                v-model="selectedArea.badZones"
                                :options="bypassOptions"
                              />
                            </b-form-group>
                          </b-col>

                        <b-col cols="12" md="6">
                          <b-form-group
                                :label="t('dmp.areaInformation.burglaryBellOutput')"
                                label-for="burglaryBellOutput"
                              >
                                <div class="d-flex gap-2">
                                  <b-form-select
                                    v-model="selectedAreaBurglaryPrefix"
                                    :options="bellOutputPrefixOptions"
                                    style="max-width: 60px"
                                    class="mr-1"
                                    @change="handleAreaBurglaryPrefixChange"
                                  />
                                  <b-form-input
                                    v-model="selectedAreaBurglaryNumber"
                                    type="text"
                                    :maxlength="selectedAreaBurglaryPrefix ? 2 : 3"
                                    @input="validateAreaBurglaryOutput"
                                    @blur="validateAreaBurglaryOutput"
                                  />
                                </div>
                              </b-form-group>
                        </b-col>

                          <b-col cols="12" md="6">
                            <b-form-group
                              :label="t('dmp.areaInformation.armedOutput')"
                              label-for="armedOutput"
                            >
                              <b-form-input
                                id="armedOutput"
                                v-model="selectedArea.armedOutput"
                                type="number"
                                min="0"
                                max="999"
                                :placeholder="t('dmp.areaInformation.armedOutput')"
                              />
                            </b-form-group>
                          </b-col>

                          <b-col cols="12" md="6">
                            <b-form-group
                              :label="t('dmp.areaInformation.lateOutput')"
                              label-for="lateOutput"
                            >
                              <b-form-input
                                id="lateOutput"
                                v-model="selectedArea.lateOutput"
                                type="number"
                                min="0"
                                max="999"
                                :placeholder="t('dmp.areaInformation.lateOutput')"
                              />
                            </b-form-group>
                          </b-col>

                          <b-col cols="12" md="6">
                            <b-form-group
                              :label="t('dmp.areaInformation.lateArmDelay')"
                              label-for="lateArmDelay"
                            >
                              <b-form-input
                                id="lateArmDelay"
                                v-model="selectedArea.lateArmDelay"
                                type="number"
                                min="0"
                                max="999"
                                :placeholder="t('dmp.areaInformation.lateArmDelay')"
                              />
                            </b-form-group>
                          </b-col>

                          <b-col cols="12" md="6">
                            <b-form-group
                              :label="t('dmp.areaInformation.twoManRule')"
                              label-for="twoManRule"
                            >
                              <b-form-select
                                id="twoManRule"
                                v-model="selectedArea.twoManRule"
                                :options="noYesOptions"
                              />
                            </b-form-group>
                          </b-col>

                          <b-col cols="12">
                            <div class="mt-2">
                              <h6 class="font-weight-semibold mb-2">
                                {{ t("dmp.areaInformation.options") }}
                              </h6>
                              <b-row>
                                <b-col cols="12" md="6">
                                  <div class="d-flex flex-column gap-1">
                                    <b-form-checkbox
                                      v-model="selectedArea.openCloseReports"
                                    >
                                      {{ t("dmp.areaInformation.openCloseReports") }}
                                    </b-form-checkbox>
                                    <b-form-checkbox v-model="selectedArea.autoArming">
                                      {{ t("dmp.areaInformation.autoArming") }}
                                    </b-form-checkbox>
                                    <b-form-checkbox v-model="selectedArea.autoDisarm">
                                      {{ t("dmp.areaInformation.autoDisarm") }}
                                    </b-form-checkbox>
                                  </div>
                                </b-col>
                                <b-col cols="12" md="6">
                                  <div class="d-flex flex-column gap-1">
                                    <b-form-checkbox
                                      v-model="selectedArea.bankSafeAndVault"
                                    >
                                      {{ t("dmp.areaInformation.bankSafeAndVault") }}
                                    </b-form-checkbox>
                                    <b-form-checkbox v-model="selectedArea.commonArea">
                                      {{ t("dmp.areaInformation.commonArea") }}
                                    </b-form-checkbox>
                                    <b-form-checkbox v-model="selectedArea.armFirst">
                                      {{ t("dmp.areaInformation.armFirst") }}
                                    </b-form-checkbox>
                                    <b-form-checkbox v-model="selectedArea.cardPin">
                                      {{ t("dmp.areaInformation.cardPin") }}
                                    </b-form-checkbox>
                                  </div>
                                </b-col>
                              </b-row>
                            </div>
                          </b-col>

                          <!-- Action Buttons -->
                          <b-col cols="12" class="d-flex justify-content-end mt-2">
                            <b-button
                              v-if="isEditingArea"
                              variant="outline-danger"
                              size="sm"
                              class="mr-1"
                              @click="deleteArea"
                              :disabled="isSaving || isDeletingArea"
                            >
                              <b-spinner v-if="isDeletingArea" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="TrashIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>{{ t("actions.delete") }}</span>
                            </b-button>
                            <b-button
                              variant="primary"
                              size="sm"
                              type="submit"
                              :disabled="isSaving || isDeletingArea"
                            >
                              <b-spinner v-if="isSaving" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="SaveIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>{{
                                isSaving ? t("actions.saving") : t("actions.save")
                              }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  inject,
  getCurrentInstance,
  watch,
} from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import TemplateConfig from "@/views/risk/templates/TemplateConfig.vue";
import Swal from "sweetalert2";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "ConfigAreaInformation",
  components: {
    TemplateConfig,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    source: String,
    panelId: {
      type: [Number, String],
      required: true,
    },
    globalAreaConfig: {
      type: Object,
      default: () => ({}),
    },
    areasData: {
      type: Array,
      default: () => [],
    },
    panelAccount: {
      type: [String, Number],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();
    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);

    const areas = ref([]);
    const isLoading = computed(() => props.loading);
    const isSaving = ref(false);
    const isSavingGlobal = ref(false);
    const isDeletingArea = ref(false);

    const areaFormObserver = ref(null);
    const isEditingArea = ref(false);
    const editingAreaIndex = ref(-1);
    const selectedArea = ref(null);

    // Global Settings
    const globalSettings = ref({
      exitDelay: 15,
      burglaryBellOutput: "0",
      earlyMorningAmbush: 0,
      closingCheck: false,
      areaSchedules: false,
      closingCode: true,
      anyBypass: false,
    });

    const burglaryBellOutputPrefix = ref("");
    const burglaryBellOutputNumber = ref("0");

    const areaTableFields = computed(() => [
      { key: "sourceId", label: t("dmp.areaInformation.areaNumber"), sortable: true },
      { key: "name", label: t("dmp.name"), sortable: true },
    ]);

    const bypassOptions = computed(() => [
      { text: t("dmp.areaInformation.bypassOptions.bypass"), value: "B" },
      { text: t("dmp.areaInformation.bypassOptions.forceArm"), value: "F" },
      { text: t("dmp.areaInformation.bypassOptions.refuseArm"), value: "R" },
    ]);

    const noYesOptions = computed(() => [
      { text: t("dmp.areaInformation.noYes.no"), value: "N" },
      { text: t("dmp.areaInformation.noYes.yes"), value: "Y" },
    ]);

    const bellOutputPrefixOptions = [
      { value: "", text: "" },
      { value: "D", text: "D" },
      { value: "F", text: "F" },
      { value: "G", text: "G" },
    ];

    const selectedAreaBurglaryPrefix = ref("");
    const selectedAreaBurglaryNumber = ref("0");

    const handleAreaBurglaryPrefixChange = () => {
      const prefix = selectedAreaBurglaryPrefix.value;
      if (prefix) {
        selectedAreaBurglaryNumber.value = "1";
        selectedArea.value.burglaryBellOutput = prefix + "1";
      } else {
        selectedAreaBurglaryNumber.value = "0";
        selectedArea.value.burglaryBellOutput = "0";
      }
    };

    const validateAreaBurglaryOutput = () => {
      let number = selectedAreaBurglaryNumber.value.replace(/\D/g, "");
      const prefix = selectedAreaBurglaryPrefix.value;
      const padding = prefix ? 2 : 3;
      number = number.slice(0, padding);
      selectedAreaBurglaryNumber.value = number;
      selectedArea.value.burglaryBellOutput = prefix + (number || "0".repeat(padding));
    };

    const handleBurglaryPrefixChange = () => {
      const prefix = burglaryBellOutputPrefix.value;
      if (prefix) {
        burglaryBellOutputNumber.value = "1";
        globalSettings.value.burglaryBellOutput = prefix + "1";
      } else {
        burglaryBellOutputNumber.value = "000";
        globalSettings.value.burglaryBellOutput = "000";
      }
    };

    const validateBurglaryOutput = () => {
      let number = burglaryBellOutputNumber.value.replace(/\D/g, "");
      const prefix = burglaryBellOutputPrefix.value;
      const padding = prefix ? 2 : 3;
      number = number.slice(0, padding);
      burglaryBellOutputNumber.value = number;
      globalSettings.value.burglaryBellOutput = prefix + (number || "0".repeat(padding));
    };

    // Carga los Global Settings (objeto) desde props. Separado de loadAreas
    // para que recargar la lista de áreas no resetee el formulario global
    // (en modo template el form es la fuente de verdad hasta "Guardar Template").
    const loadGlobalSettings = () => {
      if (!props.globalAreaConfig) return;
      const globalConfig = props.globalAreaConfig;
      const bellValue = globalConfig.burglaryBellOutput || "000";
      const bellMatch = bellValue.match(/^([DFG])?(\d+)$/);
      if (bellMatch) {
        burglaryBellOutputPrefix.value = bellMatch[1] || "";
        const padding = bellMatch[1] ? 2 : 3;
        burglaryBellOutputNumber.value = bellMatch[2].padStart(padding, "0");
      } else {
        burglaryBellOutputPrefix.value = "";
        burglaryBellOutputNumber.value = "000";
      }

      globalSettings.value = {
        exitDelay: parseInt(globalConfig.exitDelay) || 0,
        burglaryBellOutput: bellValue,
        earlyMorningAmbush: parseInt(globalConfig.earlyMorningAmbush) || 0,
        closingCheck: globalConfig.closingCheck === "Y",
        areaSchedules: globalConfig.areaSchedules === "Y",
        closingCode: globalConfig.closingCode === "Y",
        anyBypass: globalConfig.anyBypass === "Y",
      };
    };

    const loadAreas = () => {
      if (!props.areasData) {
        areas.value = [];
        return;
      }

      try {
        // Cargar áreas desde props
        if (props.areasData && props.areasData.length > 0) {
        areas.value = props.areasData
              .filter((area) => area.name?.trim() !== "*UNUSED*")
              .map((area) => ({
            sourceId: area.sourceId,
            areaNumber: area.sourceId,
            name: area.name,
            accountNumber: area.accountNumber || props.panelAccount || null,
            badZones: area.badZones || "B",
            armedOutput: parseInt(area.armedOutput) || 0,
            lateOutput: parseInt(area.lateOutput) || 0,
            lateArmDelay: parseInt(area.lateArmDelay) || null,
            twoManRule: area.dualAuthority || "N",
            openCloseReports: area.openCloseReports === "Y",
            autoArming: area.autoArm === "Y",
            autoDisarm: area.autoDisarm === "Y",
            bankSafeAndVault: area.bankSafeVault === "Y",
            commonArea: area.commonArea === "Y",
            armFirst: area.armFirst === "Y",
            cardPin: area.cardPin === "Y",
            burglaryBellOutput: area.burglaryBellOutput || "000",
          }));
        } else {
          areas.value = [];
        }
      } catch (error) {
        console.error("Error loading areas:", error);
        areas.value = [];
      }
    };

    // Construye global_area_config en forma GET para el template.
    const buildGlobalTemplateItem = () => {
      const gs = globalSettings.value;
      const pad = (v, n) => (parseInt(v) || 0).toString().padStart(n, "0");
      return {
        ...(props.globalAreaConfig || {}),
        anyBypass: gs.anyBypass ? "Y" : "N",
        exitDelay: pad(gs.exitDelay, 3),
        closingCode: gs.closingCode ? "Y" : "N",
        closingCheck: gs.closingCheck ? "Y" : "N",
        areaSchedules: gs.areaSchedules ? "Y" : "N",
        earlyMorningAmbush: pad(gs.earlyMorningAmbush, 2),
      };
    };

    // Global Settings no tiene reglas de validación (igual que saveGlobalSettings).
    const validateTemplateSection = async () => true;

    const saveGlobalSettings = async () => {
      if (isTemplateMode.value) {
        const gs = globalSettings.value;
        const pad = (v, n) => (parseInt(v) || 0).toString().padStart(n, "0");
        const globalItem = {
          ...(props.globalAreaConfig || {}),
          anyBypass: gs.anyBypass ? "Y" : "N",
          exitDelay: pad(gs.exitDelay, 3),
          closingCode: gs.closingCode ? "Y" : "N",
          closingCheck: gs.closingCheck ? "Y" : "N",
          areaSchedules: gs.areaSchedules ? "Y" : "N",
          earlyMorningAmbush: pad(gs.earlyMorningAmbush, 2),
        };
        emitTemplateUpdate("global_area_config", globalItem);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.areaInformation.messages.globalSettingsSaved"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        return;
      }

      isSavingGlobal.value = true;
      try {
        const payload = {
          exitDelay: globalSettings.value.exitDelay?.toString() || "0",
          closingCheck: globalSettings.value.closingCheck ? "Y" : "N",
          closingCode: globalSettings.value.closingCode ? "Y" : "N",
          anyBypass: globalSettings.value.anyBypass ? "Y" : "N",
          areaSchedules: globalSettings.value.areaSchedules ? "Y" : "N",
          earlyMorningAmbush: globalSettings.value.earlyMorningAmbush?.toString() || "0",
        };

        store.commit("dmp_panels/setOperationActive", true);

        const response = await store.dispatch("dmp_panels/setAreaGlobalSettings", {
          uuid: props.uuid,
          payload: payload,
        });

        if (response.success) {
          emit("operation-completed");
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.areaInformation.messages.globalSettingsSaved"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
        } else {
          throw new Error(response.message || "Failed to save global settings");
        }
      } catch (error) {
        console.error("Error saving global settings:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.areaInformation.messages.globalSettingsError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSavingGlobal.value = false;
      }
    };

    const addNewArea = () => {
      if (areas.value.length >= 99) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.areaInformation.messages.limitReached"),
            text: proxy.$t("dmp.areaInformation.messages.maxAreasAllowed"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      isEditingArea.value = false;
      editingAreaIndex.value = -1;
      selectedArea.value = {
        sourceId: null,
        name: "",
        accountNumber: props.panelAccount || null,
        badZones: "B",
        armedOutput: 0,
        lateOutput: 0,
        lateArmDelay: 60,
        twoManRule: "N",
        openCloseReports: true,
        autoArming: false,
        autoDisarm: false,
        bankSafeAndVault: false,
        commonArea: false,
        armFirst: false,
        cardPin: false,
      };
    };

    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];
        const bellValue = selected.burglaryBellOutput || "";
        const bellMatch = bellValue.match(/^([DFG])?(\d+)$/);
        if (bellMatch) {
          selectedAreaBurglaryPrefix.value = bellMatch[1] || "";
          const number = parseInt(bellMatch[2], 10);
            selectedAreaBurglaryNumber.value = number === 0 ? "0" : String(number); 
        } else {
          selectedAreaBurglaryPrefix.value = "";
          selectedAreaBurglaryNumber.value = "";
}
        isEditingArea.value = true;
        editingAreaIndex.value = areas.value.findIndex(
          (a) => a.areaNumber === selected.areaNumber || a.sourceId === selected.sourceId
        );
        selected.sourceId = parseInt(selected.sourceId);
        selectedArea.value = JSON.parse(JSON.stringify(selected));
      } else {
        selectedArea.value = null;
        isEditingArea.value = false;
      }
    };

    const deleteArea = async () => {
      if (!isEditingArea.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("messages.delete.title"),
        text: proxy.$t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const sa = selectedArea.value;
        const list = (props.areasData || []).filter(
          (a) => parseInt(a.sourceId) !== parseInt(sa.sourceId)
        );
        emitTemplateUpdate("areas", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.areaInformation.messages.areaDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedArea.value = null;
        isEditingArea.value = false;
        return;
      }

      isDeletingArea.value = true;

      store.commit("dmp_panels/setOperationActive", true);

      try {
        const areaToDelete = selectedArea.value;

        const response = await store.dispatch("dmp_panels/deleteArea", {
          uuid: props.uuid,
          areaNumber: areaToDelete.areaNumber || areaToDelete.sourceId,
        });

        if (response.success) {
          emit("operation-completed");
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.areaInformation.messages.areaDeleteSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedArea.value = null;
          isEditingArea.value = false;
        } else {
          throw new Error(response.message || "Failed to delete area");
        }
      } catch (error) {
        console.error("Error deleting area:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.areaInformation.messages.areaDeleteError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isDeletingArea.value = false;
      }
    };

    // Build an area item in the exact GET shape (panel-config / template
    // JSON): "Y"/"N" booleans, zero-padded numeric strings. The original GET
    // item is spread first so id/zones/status (not modeled by the form) are
    // preserved untouched.
    const buildAreaGetItem = () => {
      const sa = selectedArea.value;
      const pad = (v, n) => (parseInt(v) || 0).toString().padStart(n, "0");
      const sourceId = String(sa.sourceId).padStart(2, "0");
      const original = (props.areasData || []).find(
        (a) => parseInt(a.sourceId) === parseInt(sa.sourceId)
      );

      return {
        ...(original || {}),
        name: (sa.name || "").toUpperCase(),
        autoArm: sa.autoArming ? "Y" : "N",
        cardPin: sa.cardPin ? "Y" : "N",
        armFirst: sa.armFirst ? "Y" : "N",
        badZones: sa.badZones,
        sourceId,
        autoDisarm: sa.autoDisarm ? "Y" : "N",
        commonArea: sa.commonArea ? "Y" : "N",
        lateOutput: pad(sa.lateOutput, 3),
        armedOutput: pad(sa.armedOutput, 3),
        bankSafeVault: sa.bankSafeAndVault ? "Y" : "N",
        dualAuthority: sa.twoManRule,
        lateArmDelay: pad(sa.lateArmDelay, 3),
        openCloseReports: sa.openCloseReports ? "Y" : "N",
        burglaryBellOutput: sa.burglaryBellOutput || "",
      };
    };

    const saveArea = async () => {
      const isValid = await areaFormObserver.value.validate();
      if (!isValid) {
        return;
      }

      if (isTemplateMode.value) {
        const item = buildAreaGetItem();
        const list = [...(props.areasData || [])];
        const idx = list.findIndex(
          (a) => parseInt(a.sourceId) === parseInt(item.sourceId)
        );
        if (idx !== -1) {
          item.id = list[idx].id != null ? list[idx].id : nextSequentialId(list);
          list.splice(idx, 1, item);
        } else {
          item.id = nextSequentialId(list);
          list.push(item);
        }

        emitTemplateUpdate("areas", list);
        toast({
          component: ToastificationContent,
          props: {
            title: isEditingArea.value
              ? proxy.$t("dmp.areaInformation.messages.areaUpdatedSuccess")
              : proxy.$t("dmp.areaInformation.messages.areaAddedSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedArea.value = null;
        isEditingArea.value = false;
        return;
      }

      const formattedAreaNumber = selectedArea.value.sourceId.toString().padStart(2, "0");

      const areaName = selectedArea.value.name.toUpperCase();

      const areaToSave = {
        areaNumber: formattedAreaNumber,
        name: areaName,
        autoArm: selectedArea.value.autoArming ? "Y" : "N",
        badZones: selectedArea.value.badZones,
        autoDisarm: selectedArea.value.autoDisarm ? "Y" : "N",
        armedOutput: selectedArea.value.armedOutput.toString().padStart(3, "0"),
        bankSafeVault: selectedArea.value.bankSafeAndVault ? "Y" : "N",
        commonArea: selectedArea.value.commonArea ? "Y" : "N",
        dualAuthority: selectedArea.value.twoManRule,
        armFirst: selectedArea.value.armFirst ? "Y" : "N",
        lateOutput: selectedArea.value.lateOutput.toString().padStart(3, "0"),
        lateArmDelay: selectedArea.value.lateArmDelay.toString().padStart(3, "0"),
        openCloseReports: selectedArea.value.openCloseReports ? "Y" : "N",
        cardPin: selectedArea.value.cardPin ? "Y" : "N",
        burglaryBellOutput: selectedArea.value.burglaryBellOutput || "000",
      };

      isSaving.value = true;
      store.commit("dmp_panels/setOperationActive", true);
      try {
        const response = await store.dispatch("dmp_panels/setArea", {
          uuid: props.uuid,
          areaData: areaToSave,
        });

        if (response.success) {
          emit("operation-completed");
          toast({
            component: ToastificationContent,
            props: {
              title: isEditingArea.value
                ? proxy.$t("dmp.areaInformation.messages.areaUpdatedSuccess")
                : proxy.$t("dmp.areaInformation.messages.areaAddedSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedArea.value = null;
          isEditingArea.value = false;
        } else {
          throw new Error(response.message || "Failed to save area");
        }
      } catch (error) {
        console.error("Error saving area:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.areaInformation.messages.areaSaveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSaving.value = false;
      }
    };

    watch(
      () => props.areasData,
      (newData) => {
        if (newData) {
          loadAreas();
        }
      },
      { deep: true }
    );

    // En modo template el form global es la fuente de verdad: no se recarga al
    // cambiar la prop (solo al montar). En panel real sí refleja el estado del
    // servidor tras un sync.
    watch(
      () => props.globalAreaConfig,
      (newData) => {
        if (newData && !isTemplateMode.value) {
          loadGlobalSettings();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadGlobalSettings();
      loadAreas();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("global_area_config", {
          validate: validateTemplateSection,
          collect: buildGlobalTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("global_area_config");
      }
    });

    return {
      areas,
      globalSettings,
      isLoading,
      isSaving,
      isSavingGlobal,
      isDeletingArea,
      selectedArea,
      isEditingArea,
      editingAreaIndex,
      areaFormObserver,
      areaTableFields,
      bypassOptions,
      noYesOptions,
      bellOutputPrefixOptions,
      burglaryBellOutputPrefix,
      burglaryBellOutputNumber,
      handleBurglaryPrefixChange,
      validateBurglaryOutput,
      selectedAreaBurglaryPrefix,
      selectedAreaBurglaryNumber,
      handleAreaBurglaryPrefixChange,
      validateAreaBurglaryOutput,
      addNewArea,
      onRowSelected,
      deleteArea,
      saveArea,
      saveGlobalSettings,
      loadAreas,
      isTemplateMode,

      refFormObserver,
      getValidationState,

      t,
      toast,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}

.mb-75 {
  margin-bottom: 0.75rem;
}

.font-weight-semibold {
  font-weight: 600;
}

// Animación de rotación para el botón de reload
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin-animation {
  animation: spin 1s linear infinite;
}
</style>
